import { useRef, useCallback, useEffect } from "react";
import { X, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { Button } from "./Button";
import { trackImageShared, trackImageGenerated } from "../lib/analytics";
import { useLanguage } from "../contexts/LanguageContext";
import { useToast } from "../contexts/ToastContext";

interface StoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  inviteLink: string;
  squadName?: string;
}

// Decorative background shapes configuration
const DECORATIVE_SHAPES = [
  {
    className:
      "absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2",
  },
  {
    className:
      "absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2",
  },
  {
    className:
      "absolute top-1/3 left-0 w-20 h-20 bg-white/5 rounded-full -translate-x-1/2",
  },
  {
    className:
      "absolute bottom-1/4 right-0 w-28 h-28 bg-yellow-400/10 rounded-full translate-x-1/2",
  },
  {
    className:
      "absolute top-1/2 left-1/4 w-16 h-16 bg-white/5 rounded-full blur-xl",
  },
  {
    className:
      "absolute bottom-1/3 right-1/4 w-20 h-20 bg-yellow-400/10 rounded-full blur-xl",
  },
] as const;

export function StoryModal({
  isOpen,
  onClose,
  inviteLink,
  squadName,
}: StoryModalProps) {
  const storyRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();
  const { showToast } = useToast();

  // Detect if this is a squad share
  const isSquadShare = inviteLink.includes("/squad/") && squadName;

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleDownload = useCallback(async () => {
    if (!storyRef.current) {
      showToast(t("common.downloadFailed"), "error");
      return;
    }

    try {
      // Wait for layout to settle and ensure QR code is rendered
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!storyRef.current) {
        showToast(t("common.downloadFailed"), "error");
        return;
      }

      // Store original border radius and remove it for Instagram compatibility
      const originalBorderRadius = storyRef.current.style.borderRadius;
      const originalClassName = storyRef.current.className;

      // Remove rounded classes and force border-radius to 0
      storyRef.current.className = originalClassName.replace(
        /rounded-\w+/g,
        ""
      );
      storyRef.current.style.borderRadius = "0px";
      storyRef.current.style.setProperty("border-radius", "0px", "important");

      // Wait a moment for style changes to apply
      await new Promise((resolve) => setTimeout(resolve, 50));

      try {
        // Generate image with high quality settings
        // Using pixelRatio 3 for better quality (3x resolution)
        const dataUrl = await toPng(storyRef.current, {
          quality: 1.0,
          pixelRatio: 3,
          backgroundColor: undefined,
          cacheBust: true,
        });

        // Restore original border radius
        storyRef.current.style.borderRadius = originalBorderRadius;

        // Create and trigger download
        const link = document.createElement("a");
        const filename = isSquadShare
          ? `afcon-2025-${
              squadName?.toLowerCase().replace(/\s+/g, "-") || "squad"
            }-invite.png`
          : "afcon-2025-invite.png";
        link.download = filename;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Track analytics
        trackImageGenerated("story");
        trackImageShared("story");

        showToast(t("common.downloadSuccess"), "success");
      } finally {
        // Always restore border radius and class even if there's an error
        storyRef.current.className = originalClassName;
        storyRef.current.style.borderRadius = originalBorderRadius;
        storyRef.current.style.removeProperty("border-radius");
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error("Download failed:", error);
      }
      showToast(t("common.downloadFailed"), "error");
    }
  }, [storyRef, t, showToast, isSquadShare, squadName]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold">{t("story.title")}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-smooth"
              aria-label={t("common.close") || "Close"}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/90 text-sm">{t("story.subtitle")}</p>
        </div>

        {/* Story Preview */}
        <div className="px-6 pt-6 pb-4">
          <div
            ref={storyRef}
            className="gradient-primary rounded-2xl shadow-xl relative overflow-hidden"
            style={{
              aspectRatio: "9/16",
              width: "100%",
              minHeight: "500px",
            }}
          >
            {/* Decorative Background Shapes */}
            {DECORATIVE_SHAPES.map((shape, index) => (
              <div key={index} className={shape.className} aria-hidden="true" />
            ))}

            {/* Content */}
            <div className="relative z-10 h-full flex flex-col items-center justify-between px-5 py-8 sm:px-6 sm:py-10 text-white">
              {/* Top Section */}
              <div className="text-center w-full space-y-2.5 pt-2 sm:pt-4 flex-shrink-0">
                <div className="text-6xl sm:text-7xl mb-1 leading-none">🏆</div>
                <h1 className="text-2xl sm:text-3xl font-bold mb-0.5 tracking-tight">
                  {t("story.afcon")}
                </h1>
                {isSquadShare ? (
                  <div className="space-y-1.5">
                    <p className="text-base sm:text-lg opacity-95 font-semibold max-w-[280px] mx-auto px-2">
                      {squadName}
                    </p>
                    <p className="text-sm sm:text-base opacity-90 leading-relaxed max-w-[280px] mx-auto px-2">
                      {t("story.subtitle")}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm sm:text-base opacity-95 leading-relaxed max-w-[280px] mx-auto px-2">
                    {t("story.subtitle")}
                  </p>
                )}
              </div>

              {/* Middle Section - QR Code */}
              <div className="flex-1 flex items-center justify-center w-full py-2 sm:py-4 min-h-0">
                <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-2xl ring-4 ring-white/20 transform hover:scale-[1.02] transition-transform duration-300">
                  <QRCodeSVG
                    value={inviteLink}
                    size={200}
                    level="H"
                    includeMargin={false}
                    fgColor="#00864a"
                    bgColor="#ffffff"
                  />
                </div>
              </div>

              {/* Bottom Section */}
              <div className="text-center w-full space-y-1.5 pb-1 sm:pb-2 flex-shrink-0">
                <div className="bg-yellow-400 text-primary-dark px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-sm sm:text-base mb-1 shadow-xl inline-block">
                  {isSquadShare
                    ? t("story.scanToJoinSquad") || t("story.scanToJoin")
                    : t("story.scanToJoin")}
                </div>
                <p className="text-xs sm:text-sm opacity-80 font-medium">
                  {isSquadShare
                    ? t("story.competeSquad") || t("story.compete")
                    : t("story.compete")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6 space-y-2">
          <Button
            variant="primary"
            fullWidth
            onClick={handleDownload}
            className="!py-3"
          >
            <div className="flex items-center justify-center gap-2">
              <Download className="w-5 h-5" />
              <span>{t("story.download")}</span>
            </div>
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={onClose}
            className="!py-3"
          >
            {t("story.close")}
          </Button>
        </div>
      </div>
    </div>
  );
}
